
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { NodeType } from '@amplify/amp-module';
import { useNodeUtils } from '@/composables/useNodeUtils';
import { useStoreInNodes } from '@/composables/useStoreInNodes';

import NodeWrapper from '@/components/graph/common/NodeWrapper.vue';
import AppSwitch from '@/components/common/Switch.vue';
import InfoPopover from '@/components/common/InfoPopover.vue';
import { SaveSelectedNode, ToggleMediaUploadPopup } from '@/constants/mutations';
import { debounce } from '@/utils/debounce';
import { NodeSizes } from '@/constants/builder';
import { fileNameToDisplay } from '@/utils';

export default {
  components: { InfoPopover, AppSwitch, NodeWrapper },

  setup() {
    const nodeType = NodeType.player;
    const nodeActions = [{ click: true, key: 'bottom' }];
    const maxStorageSize = NodeSizes[nodeType].width - 3;

    const { store } = useStoreInNodes();
    const {
      ampData,
      uiData,
      nodeId,
      nodeRef,
      nodeSelf,
      canEditAmp,
      addStorageOption,
      deleteStorage,
      emitNodeRenderDoneEvent,
      updateDelay,
    } = useNodeUtils({ nodeType });

    const hoveringIcon = ref(false);
    const headline = ref('');
    const description = ref('');
    const callbacks = ref({});
    const readyElements = ref(0);
    const nbOfElements = ref(3); // 2 text areas and 1 image

    const isDone = computed(
      () => !!(ampData.value.media?.title && ampData.value.media?.url)
    );
    const fileName = computed(() => fileNameToDisplay(ampData.value.media?.title));

    onMounted(() => {
      headline.value = ampData.value.headline;
      description.value = ampData.value.description;
    });

    const onTextChange = (e) => {
      if (!canEditAmp.value) {
        return;
      }

      const { name, value } = e.target;
      const callback = `${name}_callback`;

      if (callbacks.value[callback]) {
        callbacks.value[callback].cancel();
      }

      callbacks.value[callback] = debounce(() => {
        ampData.value = { ...ampData.value, [name]: value };
      }).execute();
    };

    const onHeadlineKeyDown = (e) => {
      // Disable newline in headline
      if (e.keyCode === 13 || e.key.toLowerCase() === 'enter') {
        e.preventDefault();
      }
    };

    const showMediaPopup = () => {
      if (!canEditAmp.value) {
        return;
      }

      store.value.commit(`popupStore/${SaveSelectedNode}`, { data: ampData.value, type: nodeType });
      store.value.commit(`popupStore/${ToggleMediaUploadPopup}`, true);
    };

    const onAutoplayChange = (value) => {
      if (!canEditAmp.value) {
        return;
      }

      ampData.value.autoPlay = value;
    };

    const onElementReady = async () => {
      readyElements.value += 1;

      if (readyElements.value === nbOfElements.value) {
        await nextTick();

        emitNodeRenderDoneEvent();
      }
    };

    watch(
      ampData,
      (newObj) => {
        // resize node
        if (newObj.media.title && newObj.media.url) {
          const { width } = nodeSelf.value.size();
          let height = NodeSizes[nodeType].height + 51;

          if (uiData.value.selected) {
            height += 28;
          }

          nodeSelf.value.resize(width, height);
        }
      },
      { deep: true }
    );

    return {
      ampData,
      canEditAmp,
      description,
      fileName,
      headline,
      hoveringIcon,
      isDone,
      maxStorageSize,
      nodeActions,
      nodeId,
      nodeRef,
      nodeType,
      uiData,
      addStorageOption,
      deleteStorage,
      onAutoplayChange,
      onElementReady,
      onHeadlineKeyDown,
      onTextChange,
      showMediaPopup,
      updateDelay,
    };
  },
};
